import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { WARRANTY_HEALTH_URL, WARRANTY_SUBSCRIPTION_URL } from "../../Shared/constant";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import WarrantyPage from "../common/WarrantyPage";
import { usePagedList } from "../common/usePagedList";
import { listExceptions } from "../common/warrantyApi";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { knownMessageForCode } from "../common/errorMessages";
import { formatDateTime, formatMinor } from "../common/format";
import {
  ACTION_TEXT,
  benefitKindLabel,
  signalLabel,
  signalSentence,
} from "../common/opsLabels";
import OpsActionDialog from "./OpsActionDialog";
import SettleCoverRefundDialog from "./SettleCoverRefundDialog";
import { WINDOWS, windowPhrase } from "./HealthDashboard";
import "../warranty.css";

// Why a row has no action: the runbook for a stopped job whose kind is not safe to retry, the
// sentence of its error code when we have one, else a plain line.
export const whyNoAction = (item) => {
  if (item.signal === "outbox_dead") {
    return "Not safe to retry from here: see the outbox runbook.";
  }
  if (item.signal === "partner_policy_never_activated") {
    return "Nothing to do from here. This cover never became active.";
  }
  return knownMessageForCode(item.errorCode) || "Nothing to do from here.";
};

const shortId = (id) => (typeof id === "string" ? `${id.slice(0, 8)}...${id.slice(-4)}` : "-");

export const ItemWhat = ({ item }) => (
  <>
    {item.kind && <div>{item.kind}</div>}
    {item.benefitKind && <div>{benefitKindLabel(item.benefitKind)}</div>}
    {item.partnerKey && <div className="warranty-muted">Insurer {item.partnerKey}</div>}
    {item.subscriptionId && (
      <div>
        <Link to={`${WARRANTY_SUBSCRIPTION_URL}/${encodeURIComponent(item.subscriptionId)}`}>
          Open subscription
        </Link>
      </div>
    )}
    {item.paymentId && <div className="warranty-muted">Payment {shortId(item.paymentId)}</div>}
    {!item.kind && !item.benefitKind && !item.subscriptionId && !item.paymentId && (
      <span className="warranty-muted">{shortId(item.id)}</span>
    )}
  </>
);

// The rows of one signal with their one action. Used by the exception list screen and, in a tab,
// by the Partner policies screen (`signal` and `onOpenItem` come from props there).
export const ExceptionTable = ({
  signal,
  windowKey,
  onlyNew,
  accessToken,
  onOpenItem,
  externalQueued,
  reloadToken = 0,
  onChanged,
}) => {
  const fetchPage = useCallback(
    (cursor) =>
      listExceptions(accessToken, signal, { window: onlyNew ? windowKey : undefined, cursor }),
    [accessToken, signal, windowKey, onlyNew]
  );
  const list = usePagedList(fetchPage, true);
  const [queued, setQueued] = useState({});
  const [acting, setActing] = useState(null);

  // A change made elsewhere on the screen (a side panel action) reads the list again.
  const firstToken = useRef(reloadToken);
  const reloadList = list.reload;
  useEffect(() => {
    if (reloadToken !== firstToken.current) {
      firstToken.current = reloadToken;
      reloadList(true);
    }
  }, [reloadToken, reloadList]);

  const done = (item) => {
    // A settle queues nothing: the reloaded row shows what follows (gone, or Refund cover again).
    if (item.action !== "settle_cover_refund") {
      setQueued((prev) => ({ ...prev, [item.id]: true }));
    }
    toast.success(`${ACTION_TEXT[item.action]?.done || "Queued"}.`);
    list.reload(true);
    if (onChanged) {
      onChanged(item);
    }
  };

  return (
    <>
      {list.status === "loading" && <Loading label="Loading the list" />}
      {list.status === "error" && (
        <ErrorState message={list.message} onRetry={() => list.reload(false)} />
      )}
      {list.status === "ready" && list.items.length === 0 && (
        <EmptyState title="Nothing here">
          <span>
            {onlyNew
              ? `Nothing new in ${windowPhrase(windowKey)}.`
              : "There is nothing in this list right now."}
          </span>
        </EmptyState>
      )}
      {list.status === "ready" && list.items.length > 0 && (
        <>
          <div className="tableView warranty-ops-table">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Since</th>
                  <th scope="col">What</th>
                  <th scope="col" className="warranty-num">
                    Amount
                  </th>
                  <th scope="col">Tries</th>
                  <th scope="col">Error code</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((item) => (
                  <tr key={`${item.signal}-${item.id}`}>
                    <td>{formatDateTime(item.since)}</td>
                    <td>
                      <ItemWhat item={item} />
                      {onOpenItem && (
                        <button
                          type="button"
                          className="warranty-inline-btn mt-1"
                          onClick={() => onOpenItem(item)}
                        >
                          Details
                        </button>
                      )}
                    </td>
                    <td className="warranty-num">
                      {item.amountMinor !== null && item.currency ? formatMinor(item.amountMinor, item.currency) : "-"}
                    </td>
                    <td>{item.attempts !== null ? item.attempts : "-"}</td>
                    <td>{item.errorCode || "-"}</td>
                    <td className="warranty-action-col">
                      {queued[item.id] || externalQueued?.[item.id] ? (
                        <span className="warranty-badge is-scheduled">Queued</span>
                      ) : item.action ? (
                        <button
                          type="button"
                          className="warranty-inline-btn is-primary"
                          onClick={() => setActing(item)}
                        >
                          {ACTION_TEXT[item.action]?.label || item.action}
                        </button>
                      ) : (
                        <span className="warranty-muted">{whyNoAction(item)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="warranty-more">
            {list.moreError && (
              <span className="warranty-field-msg is-error" role="alert">
                {list.moreError}
              </span>
            )}
            {list.nextCursor && (
              <Button className="Addbtn" onClick={list.loadMore} disabled={list.loadingMore}>
                {list.loadingMore ? "Loading" : "Load more"}
              </Button>
            )}
          </div>
        </>
      )}
      {acting?.action === "settle_cover_refund" ? (
        // A cover refund answered with another amount: two outcomes and an amount, not a plain reason.
        <SettleCoverRefundDialog
          key={acting.id}
          show
          targetId={acting.id}
          askedMinor={acting.amountMinor}
          currency={acting.currency}
          subject={`${signalLabel(acting.signal)}, item ${shortId(acting.id)}`}
          accessToken={accessToken}
          onClose={() => setActing(null)}
          onDone={() => done(acting)}
          onRefused={() => list.reload(true)}
          onUnsure={() => list.reload(true)}
        />
      ) : (
        <OpsActionDialog
          key={acting ? acting.id : "none"}
          show={acting !== null}
          action={acting?.action}
          targetId={acting?.id}
          amountMinor={acting?.amountMinor}
          currency={acting?.currency}
          subject={acting ? `${signalLabel(acting.signal)}, item ${shortId(acting.id)}` : ""}
          accessToken={accessToken}
          onClose={() => setActing(null)}
          onDone={() => {
            const item = acting;
            done(item);
          }}
          onRefused={() => list.reload(true)}
          onUnsure={() => list.reload(true)}
        />
      )}
    </>
  );
};

const ExceptionList = () => {
  const { roomId: signal } = useParams();
  const [params] = useSearchParams();
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.OPS);
  useWarrantyNav(allowed);

  const fromHealth = WINDOWS.some((item) => item.value === params.get("window"))
    ? params.get("window")
    : "24h";
  const [windowKey, setWindowKey] = useState(fromHealth);
  const [onlyNew, setOnlyNew] = useState(false);

  return (
    <WarrantyPage
      title={signalLabel(signal)}
      sub={signalSentence(signal) || "Market IN."}
      allowed={allowed}
      deniedTitle="You do not have access to warranty health"
      deniedText="Ask an admin to give you the Warranty Operations permission."
      actions={
        <Link className="warranty-inline-btn" to={WARRANTY_HEALTH_URL}>
          Back to health
        </Link>
      }
    >
      <div className="warranty-filter formloginset">
        <Form.Check
          type="checkbox"
          id="warranty-only-new"
          label={`Only new in ${windowPhrase(windowKey)}`}
          checked={onlyNew}
          onChange={(event) => setOnlyNew(event.target.checked)}
        />
        <Form.Select
          aria-label="Window for new items"
          value={windowKey}
          onChange={(event) => setWindowKey(event.target.value)}
          disabled={!onlyNew}
        >
          {WINDOWS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Form.Select>
      </div>
      <ExceptionTable
        key={signal}
        signal={signal}
        windowKey={windowKey}
        onlyNew={onlyNew}
        accessToken={accessToken}
      />
    </WarrantyPage>
  );
};

export default ExceptionList;
